import api, { sendFile } from '../helpers/api';
import { select, all, takeEvery, call, put } from 'redux-saga/effects';
import { push } from 'react-router-redux';

import * as organizationConstants from '../constants/organizations';
import * as organizationSelectors from '../selectors/organizationSelectors';
import * as newBucketSelectors from '../selectors/newBucketSelectors';
import * as userSelectors from '../selectors/userSelectors';
import * as organizationSettingsSelectors from '../selectors/organizationSettingsSelectors';
import { getErrorMessage } from '../helpers/Util';
import BackendPaths from '../helpers/BackendPaths';
import ApplicationRoutes from '../helpers/ApplicationRoutes';

export const BUCKET_PAGE_SIZE = 21;
const DEFAULT_GROUPS_ORDER_COLUMN = 'name';

function* fetchOrganizations() {
    try {
        const organizations = yield call(
            api,
            BackendPaths.currentUserOrganizations.getPath(),
        );
        yield put({
            type: organizationConstants.FETCH_ORGANIZATIONS_SUCCESS,
            payload: organizations,
        });
    } catch (e) {
        yield put({ type: organizationConstants.FETCH_ORGANIZATIONS_FAILURE });
    }
}

function* watchFetchOrganizations() {
    yield takeEvery(
        organizationConstants.FETCH_ORGANIZATIONS_REQUEST,
        fetchOrganizations,
    );
}

function* fetchOrganizationBuckets(action) {
    const {
        organizationId,
        filterName,
        page,
        resetList,
        fetchAll,
    } = action.payload;

    // Setup for fetching all buckets
    let bucketsPath = BackendPaths.bucketsAll;
    const payload = {
        organizationId,
        name: filterName || '',
        orderColumn: DEFAULT_GROUPS_ORDER_COLUMN,
    };

    if (!fetchAll) {
        // Setup for fetching paged buckets
        bucketsPath = BackendPaths.bucketsWithPagination;
        payload.page = page || 0;
        payload.pageSize = BUCKET_PAGE_SIZE;
    }

    try {
        const buckets = yield call(api, bucketsPath.getPath(payload));
        yield put({
            type: organizationConstants.FETCH_ORGANIZATION_BUCKETS_SUCCESS,
            payload: {
                buckets: buckets.map(item => ({
                    ...item,
                    visibleInApplications: customSort(item.visibleInApplications)
                })),
                organizationId,
                page,
                resetList,
            },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.FETCH_ORGANIZATION_BUCKETS_FAILURE,
        });
    }
}

/**
 * Currently `visibleInApplications` array is used to maintain the order of
 * applications in one group. That order is based on the oreder of permission in `permissions` tab
 * in postgres. 
 * But the issue is that not always the order is so that charts are first.
 * For example, Pearson groups have `layout` projects first.
 * 
 * Note that it is possible that only some of project types may be available for some groups (that is
 * specified in dev tools when creating organizations).
 * 
 * This function will take the input array of applications, and order them in the
 * following order: ['chart', 'mapspice', 'currency', 'survey', 'layout', 'geobuffer'] 
 */
function customSort(arr) {
    const order = ['chart', 'mapspice', 'currency', 'survey', 'layout', 'geobuffer'];

    return arr.sort((a, b) => {
        return order.indexOf(a) - order.indexOf(b);
    });
}


function* watchFetchOrganizationBuckets() {
    yield takeEvery(
        organizationConstants.FETCH_ORGANIZATION_BUCKETS_REQUEST,
        fetchOrganizationBuckets,
    );
}

function* fetchBucket(action) {
    const organizationId = action.payload.organizationId;
    try {
        const bucket = yield call(
            api,
            BackendPaths.bucketUpdate.getPath({
                bucketId: action.payload.bucketId,
            }),
        );
        yield put({
            type: organizationConstants.FETCH_BUCKET_SUCCESS,
            payload: { bucket, organizationId },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.FETCH_BUCKET_FAILURE,
            payload: { organizationId },
        });
    }
}

function* watchFetchBucket() {
    yield takeEvery(organizationConstants.FETCH_BUCKET_REQUEST, fetchBucket);
}

function* fetchOrganizationMembers(action) {
    const { organizationId } = action.payload;
    try {
        const response = yield call(
            api,
            BackendPaths.organizationMembers.getPath({ organizationId }),
        );
        yield put({
            type: organizationConstants.FETCH_ORGANIZATION_MEMBERS_SUCCESS,
            payload: { members: response.members, organizationId },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.FETCH_ORGANIZATION_MEMBERS_FAILURE,
            payload: { organizationId },
        });
    }
}

function* watchFetchOrganizationMembers() {
    yield takeEvery(
        organizationConstants.FETCH_ORGANIZATION_MEMBERS_REQUEST,
        fetchOrganizationMembers,
    );
}

function* fetchOrganizationGaSettings() {
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    let response;

    try {
        response = yield call(
            api,
            BackendPaths.identitySettings.getPath({
                identityId: organizationId,
            }),
        );
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.FETCH_ORGANIZATION_GA_SETTINGS_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
    yield put({
        type: organizationConstants.FETCH_ORGANIZATION_GA_SETTINGS_SUCCESS,
        payload: { settings: response.settings, organizationId },
    });
}

function* watchFetchOrganizationGaSettings() {
    yield takeEvery(
        organizationConstants.FETCH_ORGANIZATION_GA_SETTINGS_REQUEST,
        fetchOrganizationGaSettings,
    );
}

function* createNewBucketInOrganization() {
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    const newBucket = yield select(newBucketSelectors.getState);
    const visibleInApplicationsWithoutAll = yield select(
        newBucketSelectors.getVisibleInApplicationsWithoutAll,
    );
    try {
        const bucket = yield call(api, BackendPaths.bucket.getPath(), 'POST', {
            organizationId,
            name: newBucket.bucketName,
            description: newBucket.bucketDescription,
            visibleInApplications: visibleInApplicationsWithoutAll,
        });
        yield put({
            type: organizationConstants.NEW_BUCKET_SUCCESS,
            payload: bucket,
        });
        yield put(
            push(
                ApplicationRoutes.bucketAssets.getPath({
                    organizationId: bucket.organizationId,
                    bucketId: bucket.id,
                    application: 'chart',
                }),
            ),
        );
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.NEW_BUCKET_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchCreateNewBucketInOrganization() {
    yield takeEvery(
        organizationConstants.NEW_BUCKET_REQUEST,
        createNewBucketInOrganization,
    );
}

function* deleteBucketInOrganization(action) {
    const { bucketId } = action.payload;
    try {
        yield call(
            api,
            BackendPaths.bucketUpdate.getPath({ bucketId }),
            'DELETE',
            {
                groupId: bucketId,
            },
        );
        yield put({
            type: organizationConstants.DELETE_BUCKET_SUCCESS,
            payload: { bucketId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.DELETE_BUCKET_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchDeleteBucketInOrganization() {
    yield takeEvery(
        organizationConstants.DELETE_BUCKET_REQUEST,
        deleteBucketInOrganization,
    );
}

function* updateOrganization() {
    const { newOrgName } = yield select(organizationSettingsSelectors.getState);
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );

    if (!newOrgName) {
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_SETTINGS_SKIP,
            payload: { organizationId },
        });
        return;
    }

    const update = {};
    if (newOrgName && newOrgName.trim()) {
        update.name = newOrgName.trim();
    }

    try {
        const response = yield call(
            api,
            BackendPaths.organization.getPath({ organizationId }),
            'POST',
            update,
        );
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_SETTINGS_SUCCESS,
            payload: response,
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_SETTINGS_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchUpdateOrganization() {
    yield takeEvery(
        organizationConstants.UPDATE_ORGANIZATION_SETTINGS_REQUEST,
        updateOrganization,
    );
}

function* updateOrganizationGa() {
    const settings = yield select(organizationSettingsSelectors.getState);
    const { ga } = settings;

    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );

    if (!ga) {
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_GA_SETTINGS_SKIP,
            payload: { organizationId },
        });
        return;
    }

    let response;
    try {
        response = yield call(
            api,
            BackendPaths.identitySettings.getPath({
                identityId: organizationId,
            }),
            'post',
            {
                analyticsOn: ga.analyticsOn,
                trackingId: ga.trackingId || '',
            },
        );
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_GA_SETTINGS_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
    yield put({
        type: organizationConstants.UPDATE_ORGANIZATION_GA_SETTINGS_SUCCESS,
        payload: { settings: response.settings, organizationId },
    });
}

function* watchUpdateOrganizationGa() {
    yield takeEvery(
        organizationConstants.UPDATE_ORGANIZATION_SETTINGS_REQUEST,
        updateOrganizationGa,
    );
}

function* updateOrganizationAvatar(action) {
    const { avatar } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        const response = yield call(
            sendFile,
            BackendPaths.identity.getPath({ identityId: organizationId }),
            'POST',
            {
                file: avatar,
            },
        );
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_AVATAR_SUCCESS,
            payload: { avatars: response, organizationId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.UPDATE_ORGANIZATION_AVATAR_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchUpdateOrganizationAvatar() {
    yield takeEvery(
        organizationConstants.UPDATE_ORGANIZATION_AVATAR_REQUEST,
        updateOrganizationAvatar,
    );
}

function* changeMembersRoles(action) {
    const { membersIds, isAdmin } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        const memberResponses = [];
        for (let index = 0; index < membersIds.length; index += 1) {
            const memberId = membersIds[index];
            const member = yield call(
                api,
                BackendPaths.organizationMembersUpdate.getPath(),
                'POST',
                {
                    organizationId,
                    memberId,
                    isAdmin,
                },
            );
            memberResponses.push(member);
        }
        const currentUser = yield select(userSelectors.getCurrentUser);
        const currentUserResponse = memberResponses.find(
            memberResponse => memberResponse.id === currentUser.id,
        );
        if (currentUserResponse) {
            yield put({
                type: organizationConstants.CURRENT_USER_ROLE_CHANGE_SUCCESS,
                payload: {
                    currentUserIsAdmin: currentUserResponse.isAdmin,
                    organizationId,
                },
            });
        }
        yield put({
            type: organizationConstants.CHANGE_USERS_ROLE_SUCCESS,
            payload: { members: memberResponses, organizationId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.CHANGE_USERS_ROLE_FAILURE,
            payload: { error: getErrorMessage(response) },
        });
    }
}

function* watchChangeMembersRoles() {
    yield takeEvery(
        organizationConstants.CHANGE_USERS_ROLE_REQUEST,
        changeMembersRoles,
    );
}

function* removeMembers(action) {
    const { membersIds } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        yield call(
            api,
            BackendPaths.organizationMembersRemove.getPath(),
            'DELETE',
            {
                organizationId,
                membersIds,
            },
        );
        yield put({
            type: organizationConstants.REMOVE_USERS_SUCCESS,
            payload: { membersIds, organizationId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: organizationConstants.REMOVE_USERS_FAILURE,
            payload: { error: getErrorMessage(response) },
        });
    }
}

function* watchRemoveMembers() {
    yield takeEvery(organizationConstants.REMOVE_USERS_REQUEST, removeMembers);
}

function* userSearch(action) {
    const { searchTerm } = action.payload;
    try {
        const result = yield call(
            api,
            BackendPaths.userSearch.getPath({ searchTerm }),
        );
        yield put({
            type: organizationConstants.USER_SEARCH_SUCCESS,
            payload: { result },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.USER_SEARCH_FAILURE,
        });
    }
}

function* watchUserSearch() {
    yield takeEvery(organizationConstants.USER_SEARCH_REQUEST, userSearch);
}

function* addOrganizationMembers(action) {
    const { usersToBeAdded } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        const memberResponses = [];
        for (let index = 0; index < usersToBeAdded.length; index += 1) {
            const { memberId, isAdmin } = usersToBeAdded[index];
            const member = yield call(
                api,
                BackendPaths.organizationMembersCreate.getPath(),
                'POST',
                {
                    organizationId,
                    memberId,
                    isAdmin,
                },
            );
            memberResponses.push(member);
        }
        yield put({
            type: organizationConstants.ADD_ORGANIZATION_MEMBERS_SUCCESS,
            payload: { members: memberResponses, organizationId },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.ADD_ORGANIZATION_MEMBERS_FAILURE,
        });
    }
}

function* watchAddOrganizationMembers() {
    yield takeEvery(
        organizationConstants.ADD_ORGANIZATION_MEMBERS_REQUEST,
        addOrganizationMembers,
    );
}

function* inviteOrganizationMember(action) {
    const { email, isAdmin } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        const response = yield call(
            api,
            BackendPaths.inviteMember.getPath(),
            'POST',
            {
                organizationId,
                email,
                isAdmin,
            },
        );
        yield put({
            type: organizationConstants.INVITE_ORGANIZATION_MEMBER_SUCCESS,
            payload: { invitation: response.invitation, organizationId },
        });
    } catch (e) {
        yield put({
            type: organizationConstants.INVITE_ORGANIZATION_MEMBER_FAILURE,
        });
    }
}

function* watchInviteOrganizationMember() {
    yield takeEvery(
        organizationConstants.INVITE_ORGANIZATION_MEMBER_REQUEST,
        inviteOrganizationMember,
    );
}

function* fetchOrganizationMemberInvitations(action) {
    const { organizationId } = action.payload;
    try {
        const response = yield call(
            api,
            BackendPaths.organizationInvitations.getPath({ organizationId }),
        );
        yield put({
            type:
                organizationConstants.FETCH_ORGANIZATION_MEMBER_INVITATIONS_SUCCESS,
            payload: { invitations: response, organizationId },
        });
    } catch (e) {
        yield put({
            type:
                organizationConstants.FETCH_ORGANIZATION_MEMBER_INVITATIONS_FAILURE,
            payload: { invitations: [], organizationId },
        });
    }
}

function* watchFetchOrganizationMemberInvitations() {
    yield takeEvery(
        organizationConstants.FETCH_ORGANIZATION_MEMBER_INVITATIONS_REQUEST,
        fetchOrganizationMemberInvitations,
    );
}

function* revokeOrganizationMemberInvitation(action) {
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    const { invitation } = action.payload;
    try {
        yield call(api, BackendPaths.deleteInvitation.getPath(), 'DELETE', {
            id: invitation.id,
        });
        yield put({
            type:
                organizationConstants.REVOKE_ORGANIZATION_MEMBER_INVITATIONS_SUCCESS,
            payload: { invitationId: invitation.id, organizationId },
        });
    } catch (e) {
        yield put({
            type:
                organizationConstants.REVOKE_ORGANIZATION_MEMBER_INVITATIONS_FAILURE,
            payload: { organizationId },
        });
    }
}

function* watchRevokeOrganizationMemberInvitation() {
    yield takeEvery(
        organizationConstants.REVOKE_ORGANIZATION_MEMBER_INVITATIONS_REQUEST,
        revokeOrganizationMemberInvitation,
    );
}

function* resendOrganizationMemberInvitation(action) {
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    const { invitation } = action.payload;
    try {
        yield call(api, BackendPaths.resendInvitation.getPath(), 'POST', {
            id: invitation.id,
            organizationId,
        });
        yield put({
            type:
                organizationConstants.RESEND_ORGANIZATION_MEMBER_INVITATIONS_SUCCESS,
            payload: { invitationId: invitation.id, organizationId },
        });
    } catch (e) {
        yield put({
            type:
                organizationConstants.RESEND_ORGANIZATION_MEMBER_INVITATIONS_FAILURE,
            payload: { organizationId },
        });
    }
}

function* watchResendOrganizationMemberInvitation() {
    yield takeEvery(
        organizationConstants.RESEND_ORGANIZATION_MEMBER_INVITATIONS_REQUEST,
        resendOrganizationMemberInvitation,
    );
}

export default function*() {
    yield all([
        watchFetchOrganizations(),
        watchFetchOrganizationBuckets(),
        watchFetchBucket(),
        watchFetchOrganizationMembers(),
        watchCreateNewBucketInOrganization(),
        watchDeleteBucketInOrganization(),
        watchUpdateOrganization(),
        watchUpdateOrganizationGa(),
        watchChangeMembersRoles(),
        watchRemoveMembers(),
        watchUserSearch(),
        watchAddOrganizationMembers(),
        watchUpdateOrganizationAvatar(),
        watchFetchOrganizationGaSettings(),
        watchInviteOrganizationMember(),
        watchFetchOrganizationMemberInvitations(),
        watchRevokeOrganizationMemberInvitation(),
        watchResendOrganizationMemberInvitation(),
    ]);
}



// WEBPACK FOOTER //
// ./src/sagas/organizationSagas.js